import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ChevronDown, Copy, ExternalLink, Plus } from "lucide-react";
import { format } from "date-fns";
import {
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useAuth } from "../../services/auth/AuthProvider";
import { DEEPLINK_BASE } from "../../config/app.routes";
import {
  createCampaign,
  getCampaigns,
  type CampaignVisit,
  type RawCampaign,
} from "../../services/api/campaigns";
import { getBooksForPublisher } from "../../services/api/books";
import { BookFilter } from "../statistics/BookFilter";

function visitDate(ts: CampaignVisit["timestamp"]): string {
  let ms: number | null = null;
  if (typeof ts === "number") ms = ts;
  else if (typeof ts === "string") ms = Date.parse(ts);
  else if (ts && typeof ts._seconds === "number") ms = ts._seconds * 1000;
  if (ms == null || Number.isNaN(ms)) return "—";
  return format(new Date(ms), "dd.MM.yyyy HH:mm:ss");
}

function CampaignRow({
  campaign,
  onCopy,
}: {
  campaign: RawCampaign;
  onCopy: (url: string) => void;
}) {
  const { isOpen, onToggle } = useDisclosure();
  const visits = campaign.visits ?? [];

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="blackAlpha.100"
      boxShadow="sm"
      overflow="hidden"
    >
      <Flex align="center" gap={4} p={4}>
        <Box
          flex={1}
          minW={0}
          cursor="pointer"
          onClick={onToggle}
          role="button"
          aria-expanded={isOpen}
        >
          <Text fontWeight="medium" noOfLines={1}>{campaign.name}</Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>{campaign.deeplinkUrl}</Text>
        </Box>
        <Box textAlign="center" minW="60px">
          <Text fontSize="lg" fontWeight="semibold">{visits.length}</Text>
          <Text fontSize="xs" color="gray.500">{"Візити"}</Text>
        </Box>
        <IconButton
          aria-label={"Відкрити"}
          size="sm"
          variant="outline"
          icon={<Icon as={ExternalLink} boxSize={4} />}
          onClick={() =>
            campaign.deeplinkUrl && window.open(campaign.deeplinkUrl, "_blank", "noopener")
          }
        />
        <IconButton
          aria-label="copy"
          size="sm"
          variant="outline"
          icon={<Icon as={Copy} boxSize={4} />}
          onClick={() => campaign.deeplinkUrl && onCopy(campaign.deeplinkUrl)}
        />
        <IconButton
          aria-label="details"
          size="sm"
          variant="ghost"
          onClick={onToggle}
          icon={
            <Icon
              as={ChevronDown}
              boxSize={5}
              transition="transform 0.2s"
              transform={isOpen ? "rotate(180deg)" : undefined}
            />
          }
        />
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box borderTop="1px solid" borderColor="blackAlpha.100" px={4} py={3}>
          {visits.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
              {"Переходів ще немає"}
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>{"Версія"}</Th>
                  <Th>{"Платформа"}</Th>
                  <Th>{"Дата"}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {visits.map((v, i) => (
                  <Tr key={i}>
                    <Td>{v.appVersion || "—"}</Td>
                    <Td>{v.platform || "—"}</Td>
                    <Td>{visitDate(v.timestamp)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export function CampaignsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { publishingHouse } = useAuth();
  const publisherName = publishingHouse?.name ?? "";

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [name, setName] = useState("");
  const [bookId, setBookId] = useState<string | null>(null);

  const campaignsQuery = useQuery(
    ["campaigns", publisherName],
    () => getCampaigns(publisherName),
    { enabled: !!publisherName },
  );
  const booksQuery = useQuery(
    ["books", publisherName],
    () => getBooksForPublisher(publisherName),
    { enabled: !!publisherName },
  );

  const createMutation = useMutation(
    () =>
      createCampaign({
        id: bookId as string,
        name: name.trim(),
        deeplinkUrl: `${DEEPLINK_BASE}/books/${bookId}`,
        publisher: publisherName,
      }),
    {
      onSuccess: () => {
        toast({ description: "Кампанію створено", status: "success" });
        queryClient.invalidateQueries(["campaigns", publisherName]);
        setName("");
        setBookId(null);
        onClose();
      },
      onError: () => {
        toast({ description: "Сталась помилка!", status: "error" });
      },
    },
  );

  function submit() {
    if (!name.trim()) {
      toast({ description: "Введіть назву кампанії", status: "error" });
      return;
    }
    if (!bookId) {
      toast({ description: "Виберіть книгу", status: "error" });
      return;
    }
    createMutation.mutate();
  }

  function copy(url: string) {
    navigator.clipboard.writeText(url).then(
      () => toast({ description: "Посилання скопійовано", status: "success" }),
      () => toast({ description: "Сталась помилка!", status: "error" }),
    );
  }

  const campaigns = campaignsQuery.data ?? [];

  return (
    <Flex direction="column" minH="100%">
      <Flex
        as="header"
        position="sticky"
        top={0}
        zIndex={20}
        align="center"
        justify="space-between"
        bg="white"
        borderBottom="1px solid"
        borderColor="blackAlpha.100"
        px={6}
        py={4}
      >
        <Heading size="lg" fontWeight="medium" letterSpacing="wide" textTransform="uppercase">
          {"Промо-кампанії"}
        </Heading>
        <Button leftIcon={<Icon as={Plus} boxSize={4} />} onClick={onOpen}>
          {"Додати"}
        </Button>
      </Flex>

      <Box flex={1} p={6}>
        {campaignsQuery.isLoading ? (
          <Center h="64">
            <Spinner color="brand.orange" />
          </Center>
        ) : campaigns.length === 0 ? (
          <Center h="64">
            <Text color="gray.500">{"Кампаній ще немає"}</Text>
          </Center>
        ) : (
          <VStack align="stretch" spacing={3}>
            {campaigns.map((c, i) => (
              <CampaignRow key={`${c.deeplinkUrl ?? ""}-${i}`} campaign={c} onCopy={copy} />
            ))}
          </VStack>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{"Створити кампанію"}</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">{"Назва кампанії"}</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">{"Книжка"}</FormLabel>
                <BookFilter
                  books={(booksQuery.data ?? []).map((b) => ({ id: b.id, name: b.name }))}
                  value={bookId}
                  onChange={setBookId}
                  allLabel={"Виберіть книгу"}
                  searchPlaceholder={"Пошук"}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="outline" onClick={onClose} isDisabled={createMutation.isLoading}>
              {"Скасувати"}
            </Button>
            <Button onClick={submit} isLoading={createMutation.isLoading}>
              {"Зберегти"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
